// SCARTLEAD.TV Lobby 2.0 — station communications companion.
import { createPortal } from "react-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, ChevronRight, ChevronDown, Send, Users, Radio, Pencil, Pin, X } from "lucide-react";

const CHANNEL_PALETTE = ["var(--phosphor)", "#FF3B30", "#FFCC00", "#5DD3FF", "#FF8AD8", "#A0FFA0", "#FFA94D", "#C792EA"];

function colourForChannel(id) {
  if (!id) return CHANNEL_PALETTE[0];
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) | 0;
  return CHANNEL_PALETTE[Math.abs(h) % CHANNEL_PALETTE.length];
}

function ConnDot({ status }) {
  const map = {
    open: ["LIVE", "var(--phosphor)"],
    connecting: ["CONNECTING", "#FFCC00"],
    closed: ["OFFLINE", "#FF3B30"],
    idle: ["IDLE", "#888888"],
  };
  const [label, color] = map[status] || map.idle;
  return (
    <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em]" style={{ color }}>
      <span
        className={`inline-block h-2 w-2 rounded-full ${status === "connecting" ? "animate-pulse" : ""}`}
        style={{ backgroundColor: color, boxShadow: `0 0 7px ${color}` }}
      />
      {label}
    </span>
  );
}

function fmt(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function LiveChat({
  messages,
  pinned,
  status,
  nick,
  isOwnerNick,
  channelId,
  viewerCount = 0,
  channelCounts = {},
  tunedChannelId,
  channelName,
  channelNumberLabel,
  onSend,
  onRename,
  onPin,
  onFirstSent,
}) {
  const hue = colourForChannel(channelId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [editingNick, setEditingNick] = useState(false);
  const [pinDraft, setPinDraft] = useState("");
  const [pinningEdit, setPinningEdit] = useState(false);
  const [nickDraft, setNickDraft] = useState(nick || "");
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);
  const firstSentRef = useRef(false);
  const atBottomRef = useRef(true);
  const prevCount = useRef(messages.length);

  const online = Math.max(0, Number(viewerCount) || 0);
  const watchingHere = Number(channelCounts?.[tunedChannelId] || 0);
  const recent = useMemo(() => (Array.isArray(messages) ? messages.slice(-60) : []), [messages]);
  const disabled = status !== "open";

  useEffect(() => setNickDraft(nick || ""), [nick]);

  useEffect(() => {
    const before = prevCount.current;
    const after = messages.length;
    if (!open && after > before) setUnread((u) => u + (after - before));
    prevCount.current = after;
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    if (atBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      setUnread(0);
    } else {
      setUnread((u) => u + 1);
    }
  }, [messages, open]);

  const openLobby = () => {
    setOpen(true);
    setUnread(0);
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  };

  const onScroll = (e) => {
    const el = e.currentTarget;
    const atBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < 16;
    atBottomRef.current = atBottom;
    if (atBottom) setUnread(0);
  };

  const jumpToLive = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
    setUnread(0);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    const ok = onSend(draft);
    if (ok !== false) {
      setDraft("");
      if (!firstSentRef.current) {
        firstSentRef.current = true;
        if (onFirstSent) onFirstSent();
      }
    }
  };

  const commitNick = (e) => {
    e.preventDefault();
    const value = nickDraft.trim();
    if (value && value !== nick && onRename) onRename(value);
    setEditingNick(false);
  };

  const commitPin = (e) => {
    e.preventDefault();
    if (onPin) onPin(pinDraft.trim());
    setPinningEdit(false);
  };

  const lobbyUi = (
    <div
      className={`pointer-events-auto fixed inset-y-0 right-0 flex items-center transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-[calc(100%-44px)]"}`}
      data-testid="live-chat"
      style={{ fontFamily: "VT323, monospace" }}
    >
      <button
        type="button"
        onClick={open ? () => setOpen(false) : openLobby}
        className="relative h-52 w-11 shrink-0 rounded-l-xl border-y border-l border-white/15 bg-[#111] shadow-[-8px_0_24px_rgba(0,0,0,.45)] flex flex-col items-center justify-center gap-3 hover:bg-[#171717]"
        data-testid="live-chat-toggle"
        aria-label={open ? "Close Lobby" : "Open Lobby"}
      >
        <MessageSquare size={17} className="text-[var(--phosphor)]" />
        <span className="[writing-mode:vertical-rl] rotate-180 text-[11px] tracking-[0.28em] text-[var(--phosphor)]">LOBBY</span>
        <span className="text-[9px] text-white/55">{online}</span>
        {unread > 0 && (
          <span className="absolute -left-2 top-2 min-w-5 h-5 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] flex items-center justify-center" data-testid="lobby-unread-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        <ChevronRight size={14} className={`text-white/50 transition-transform ${open ? "" : "rotate-180"}`} />
      </button>

      <section
        className="h-[min(760px,calc(100vh-32px))] w-[min(410px,calc(100vw-44px))] mr-4 rounded-xl border border-white/15 bg-[#0B0D0E]/[0.98] shadow-[-18px_18px_60px_rgba(0,0,0,.58)] overflow-hidden flex flex-col"
        data-testid="lobby-panel"
      >
        <header className="px-4 py-3 border-b border-white/10 bg-gradient-to-b from-white/[.055] to-transparent">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[.32em] text-white/40">STATION COMMUNICATIONS</div>
              <div className="mt-1 text-xl tracking-[.14em] text-[var(--phosphor)] phosphor-glow">◢ SCARTLEAD.TV LOBBY ◣</div>
            </div>
            <button onClick={() => setOpen(false)} className="h-8 w-8 rounded border border-white/10 text-white/45 flex items-center justify-center" data-testid="live-chat-collapse">
              <X size={15} />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-4">
            <ConnDot status={status} />
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[.16em] text-white/55">
              <Users size={12} /> {online} ONLINE
            </span>
          </div>
        </header>

        <div className="grid grid-cols-2 gap-2 px-4 pt-3">
          <div className="rounded border border-white/10 bg-white/[.025] p-3">
            <div className="text-[9px] tracking-[.22em] text-white/35">IN THE LOBBY</div>
            <div className="mt-1 flex items-end gap-2">
              <span className="text-2xl text-[var(--phosphor)]">{online}</span>
              <span className="pb-1 text-[10px] text-white/45">CONNECTED</span>
            </div>
          </div>
          <div className="rounded border border-white/10 bg-white/[.025] p-3">
            <div className="text-[9px] tracking-[.22em] text-white/35">YOUR CHANNEL</div>
            <div className="mt-1 flex items-end gap-2">
              <span className="text-2xl text-[var(--phosphor)]">{watchingHere}</span>
              <span className="pb-1 text-[10px] text-white/45">WATCHING</span>
            </div>
          </div>
        </div>

        <div className="px-4 pt-2">
          <div className="rounded border border-white/[.08] bg-black/30 px-3 py-2 flex items-center gap-2">
            <Radio size={13} className="text-[var(--phosphor)]" />
            <div className="min-w-0">
              <div className="text-[9px] tracking-[.18em] text-white/35">YOU'RE TUNED TO</div>
              <div className="text-[12px] tracking-[.08em] truncate text-white/70">
                {channelNumberLabel ? `CH ${channelNumberLabel} · ` : ""}{channelName || "OFF AIR"}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 pt-3">
          {pinned?.text && !pinningEdit && (
            <div className="rounded border border-[#FFCC00]/30 bg-[#FFCC00]/[.06] px-3 py-2" data-testid="chat-pinned">
              <div className="flex items-start gap-2">
                <Pin size={12} className="mt-0.5 text-[#FFCC00]" />
                <div className="flex-1">
                  <div className="text-[9px] tracking-[.2em] text-[#FFCC00]/65">STATION MESSAGE</div>
                  <div className="mt-1 text-[12px] text-[#FFCC00] break-words">{pinned.text}</div>
                </div>
                {isOwnerNick && <button onClick={() => onPin && onPin("")} className="text-[9px] text-[#FFCC00]/50" data-testid="chat-pin-clear">CLEAR</button>}
              </div>
            </div>
          )}
          {isOwnerNick && pinningEdit && (
            <form onSubmit={commitPin} className="rounded border border-[#FFCC00]/30 bg-black/40 p-2" data-testid="chat-pin-edit">
              <div className="flex gap-2">
                <input autoFocus value={pinDraft} onChange={(e) => setPinDraft(e.target.value)} maxLength={220} className="flex-1 min-w-0 bg-black/50 border border-[#FFCC00]/25 px-2 py-1 text-[12px] text-[#FFCC00]" data-testid="chat-pin-input" />
                <button type="submit" className="text-[10px] text-[#FFCC00]">SAVE</button>
              </div>
            </form>
          )}
          {isOwnerNick && !pinningEdit && (
            <button onClick={() => { setPinDraft(pinned?.text || ""); setPinningEdit(true); }} className="mt-1 text-[9px] tracking-[.15em] text-white/30" data-testid="chat-pin-open">
              {pinned?.text ? "EDIT STATION MESSAGE" : "+ POST STATION MESSAGE"}
            </button>
          )}
        </div>

        <div className="px-4 pt-3 pb-1 flex justify-between">
          <div className="text-[9px] tracking-[.26em] text-white/35">LOBBY CHAT</div>
          <div className="text-[9px] text-white/25">LIVE · GLOBAL ROOM</div>
        </div>

        <div ref={scrollRef} onScroll={onScroll} className="mx-4 mb-2 flex-1 min-h-0 overflow-y-auto rounded border border-white/[.08] bg-black/35 p-3 space-y-2" data-testid="live-chat-messages">
          {recent.length === 0 && <div className="h-full min-h-28 flex items-center justify-center text-center text-[12px] text-white/30">NO CHATTER YET<br />BE THE FIRST ON THE LINE</div>}
          {recent.map((m, i) => m.system ? (
            <div key={`sys-${m.ts}-${i}`} className="text-[11px] italic text-[#FF3B30]/70 break-words">{m.text}</div>
          ) : (
            <div key={`${m.ts}-${i}`}>
              <div className="flex items-baseline gap-2">
                <span className={`text-[12px] ${m.is_owner ? "text-[var(--phosphor)] font-bold" : ""}`} style={m.is_owner ? undefined : { color: hue }}>
                  {m.is_owner ? "★ " : ""}{m.nick}
                </span>
                <span className="text-[9px] text-white/20">{fmt(m.ts)}</span>
              </div>
              <div className="mt-0.5 pl-2 border-l border-white/[.08] text-[12px] text-white/72 break-words">{m.text}</div>
            </div>
          ))}
        </div>

        {unread > 0 && open && (
          <button onClick={jumpToLive} data-testid="jump-to-live" className="mx-4 mb-2 rounded border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] py-1.5 text-[10px] text-[var(--phosphor)] flex justify-center gap-2">
            <ChevronDown size={12} /> {unread} NEW · JUMP TO LIVE
          </button>
        )}

        <div className="border-t border-white/10 p-3 bg-white/[.018]">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[9px] tracking-[.18em] text-white/35">YOU ARE</div>
            {editingNick ? (
              <form onSubmit={commitNick}>
                <input value={nickDraft} onChange={(e) => setNickDraft(e.target.value)} onBlur={commitNick} autoFocus maxLength={18} className="w-32 bg-black/60 border border-white/15 px-2 py-0.5 text-[11px] text-white/70" data-testid="live-chat-nick-input" />
              </form>
            ) : (
              <button onClick={() => setEditingNick(true)} className="flex items-center gap-1 text-[11px] text-[var(--phosphor)]" data-testid="live-chat-nick">
                {isOwnerNick ? "★ " : ""}{nick || "…"} <Pencil size={10} />
              </button>
            )}
          </div>
          <form onSubmit={submit} className="flex gap-2">
            <input value={draft} onChange={(e) => setDraft(e.target.value)} disabled={disabled} placeholder={disabled ? "CONNECTING TO LOBBY…" : "SAY SOMETHING…"} maxLength={220} className="flex-1 min-w-0 rounded bg-black/60 border border-white/12 px-3 py-2 text-[12px] text-white/80" data-testid="live-chat-input" />
            <button type="submit" disabled={disabled || !draft.trim()} className="w-10 rounded border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] text-[var(--phosphor)] flex items-center justify-center disabled:opacity-25" data-testid="live-chat-send">
              <Send size={15} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );

  const lobbyDock = typeof document !== "undefined" ? document.getElementById("lobby-dock") : null;
  return lobbyDock ? createPortal(lobbyUi, lobbyDock) : lobbyUi;
}
